import { AxiosError } from 'axios'

import type {
  StockImportConfigModel,
  StockImportConfigRequest,
  StockImportConfigShortModel,
} from '../types/stockImports.types'

export const isDuplicateConfigError = (error: unknown) =>
  error instanceof AxiosError && error.response?.status === 409

export interface PersistConfigDeps {
  createConfig: (
    payload: StockImportConfigRequest
  ) => Promise<StockImportConfigModel>
  updateConfig: (
    id: string,
    payload: StockImportConfigRequest
  ) => Promise<StockImportConfigModel>
  getConfigs: (
    productSupplierId: string | null
  ) => Promise<StockImportConfigShortModel[]>
  confirmOverwrite: (existing: StockImportConfigShortModel) => Promise<boolean>
}

export type PersistConfigResult =
  | {
      config: StockImportConfigModel
      outcome: 'created' | 'updated' | 'adopted-overwritten'
    }
  | { config: StockImportConfigShortModel; outcome: 'adopted-kept' }

export const findConfigForStore = (
  configs: StockImportConfigShortModel[],
  storeId: string
) =>
  configs.find(
    (item) => item.store?.id === storeId && item.status !== 'ARCHIVED'
  ) ?? null

export const persistStockImportConfig = async ({
  existingConfig,
  payload,
  productSupplierId,
  deps,
}: {
  existingConfig: StockImportConfigShortModel | null
  payload: StockImportConfigRequest
  productSupplierId: string | null
  deps: PersistConfigDeps
}): Promise<PersistConfigResult> => {
  if (existingConfig) {
    return {
      config: await deps.updateConfig(existingConfig.id, payload),
      outcome: 'updated',
    }
  }

  try {
    return { config: await deps.createConfig(payload), outcome: 'created' }
  } catch (error) {
    if (!isDuplicateConfigError(error)) throw error

    let conflicting: StockImportConfigShortModel | null = null
    try {
      conflicting = findConfigForStore(
        await deps.getConfigs(productSupplierId),
        payload.storeId
      )
    } catch {
      throw error
    }

    if (!conflicting) throw error

    if (!(await deps.confirmOverwrite(conflicting))) {
      return { config: conflicting, outcome: 'adopted-kept' }
    }

    return {
      config: await deps.updateConfig(conflicting.id, payload),
      outcome: 'adopted-overwritten',
    }
  }
}
